import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { pricingStrategyService } from '../../services/financeService';
import { PricingStrategy } from '../../types/finance';
import toast from 'react-hot-toast';
import { FaLightbulb, FaSave, FaSpinner, FaTrash } from 'react-icons/fa';
import { anthropicService } from '../../services/anthropicService';

const PricingStrategyAdvisor: React.FC = () => {
  const { user } = useAuth();
  const [strategies, setStrategies] = useState<PricingStrategy[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState<PricingStrategy | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Form states
  const [businessType, setBusinessType] = useState('');
  const [productName, setProductName] = useState('');
  const [competitorInfo, setCompetitorInfo] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [businessGoals, setBusinessGoals] = useState('');
  const [recommendedStrategy, setRecommendedStrategy] = useState('');
  const [strategyDescription, setStrategyDescription] = useState('');
  const [implementationNotes, setImplementationNotes] = useState('');

  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        setLoading(true);
        const data = await pricingStrategyService.getAll();
        setStrategies(data);
      } catch (error) {
        console.error('Error fetching strategies:', error);
        toast.error('Failed to load pricing strategies');
      } finally {
        setLoading(false);
      }
    };

    fetchStrategies();
  }, []);

  const handleSelectStrategy = async (id: string) => {
    try {
      const strategy = await pricingStrategyService.getById(id);
      setSelectedStrategy(strategy);
      setBusinessType(strategy.business_type);
      setProductName(strategy.product_name);
      setRecommendedStrategy(strategy.recommended_strategy);
      setStrategyDescription(strategy.strategy_description);
      setImplementationNotes(strategy.implementation_notes || '');
    } catch (error) {
      console.error('Error fetching strategy:', error);
      toast.error('Failed to load strategy details');
    }
  };

  const handleGenerateStrategy = async () => {
    if (!user) return;
    if (!businessType || !productName || !targetMarket || !businessGoals) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setGenerating(true);
      
      const prompt = `
        As a pricing strategy expert, recommend the best pricing strategy for the following business:
        
        Business Type: ${businessType}
        Product/Service: ${productName}
        Target Market: ${targetMarket}
        Business Goals: ${businessGoals}
        Competitor Information: ${competitorInfo || 'Not provided'}
        
        Please provide:
        1. The recommended pricing strategy name (e.g., Premium Pricing, Penetration Pricing, etc.)
        2. A detailed description of the strategy and why it's appropriate for this business
        3. Practical implementation notes for how to execute this strategy
        
        Format your response as JSON with the following structure:
        {
          "strategy": "Name of the strategy",
          "description": "Detailed description of the strategy",
          "implementation": "Implementation notes"
        }
      `;
      
      const response = await anthropicService.generateText(prompt);
      
      try {
        // Extract JSON from the response
        const jsonMatch = response.match(/\{[\s\S]*\}/); 
        if (jsonMatch) {
          const jsonResponse = JSON.parse(jsonMatch[0]);
          setRecommendedStrategy(jsonResponse.strategy);
          setStrategyDescription(jsonResponse.description);
          setImplementationNotes(jsonResponse.implementation);
          toast.success('Strategy generated successfully');
        } else {
          // If JSON parsing fails, use the whole response as the description
          setRecommendedStrategy('Custom Strategy');
          setStrategyDescription(response);
          setImplementationNotes('');
          toast.error('Strategy generated but in an unexpected format');
        }
      } catch (parseError) {
        console.error('Error parsing AI response:', parseError);
        setRecommendedStrategy('Custom Strategy');
        setStrategyDescription(response);
        setImplementationNotes('');
        toast.error('Strategy generated but in an unexpected format');
      }
    } catch (error) {
      console.error('Error generating strategy:', error);
      toast.error('Failed to generate pricing strategy');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveStrategy = async () => {
    if (!user) return;
    if (!businessType || !productName || !recommendedStrategy || !strategyDescription) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const strategyData: PricingStrategy = {
        user_id: user.id,
        business_type: businessType,
        product_name: productName,
        recommended_strategy: recommendedStrategy,
        strategy_description: strategyDescription,
        implementation_notes: implementationNotes
      };

      if (selectedStrategy) {
        // Update existing strategy
        const updated = await pricingStrategyService.update(selectedStrategy.id!, strategyData);
        setStrategies(strategies.map(s => s.id === updated.id ? updated : s));
        setSelectedStrategy(updated);
        toast.success('Pricing strategy updated successfully');
      } else {
        // Create new strategy
        const created = await pricingStrategyService.create(strategyData);
        setStrategies([created, ...strategies]);
        setSelectedStrategy(created);
        toast.success('Pricing strategy saved successfully');
      }
    } catch (error) {
      console.error('Error saving strategy:', error);
      toast.error('Failed to save pricing strategy');
    }
  };

  const handleDeleteStrategy = async () => {
    if (!selectedStrategy) return;

    try {
      await pricingStrategyService.delete(selectedStrategy.id!);
      setStrategies(strategies.filter(s => s.id !== selectedStrategy.id));
      handleNewStrategy();
      toast.success('Pricing strategy deleted successfully');
    } catch (error) {
      console.error('Error deleting strategy:', error);
      toast.error('Failed to delete pricing strategy');
    }
  };

  const handleNewStrategy = () => {
    setSelectedStrategy(null);
    setBusinessType('');
    setProductName('');
    setCompetitorInfo('');
    setTargetMarket('');
    setBusinessGoals('');
    setRecommendedStrategy('');
    setStrategyDescription('');
    setImplementationNotes('');
  };

  return (
    <section className="py-12 relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')] bg-[size:50px_50px]"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Decorative elements */}
        <div className="absolute top-20 left-12 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute -bottom-32 right-12 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      
        {/* Section Title with 3D effect */}
        <div className="relative mb-8 flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 inline-block mb-2">
              Pricing Strategy Advisor
            </h2>
            <div className="h-1 w-32 rounded-full bg-gradient-to-r from-blue-400 to-purple-500"></div>
          </div>
          <button
            onClick={handleNewStrategy}
            className="px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-blue-500/50 transform hover:-translate-y-0.5"
          >
            New Strategy
          </button>
        </div>

      {loading ? (
        <div className="text-center py-8 bg-white/30 backdrop-blur-sm rounded-xl border border-white/50 shadow-lg">
          <FaSpinner className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" />
          <p className="text-gray-700">Loading your strategies...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left sidebar - Strategies list */}
          <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-10"></div>
            <h2 className="text-lg font-semibold mb-4 text-gray-800">Your Strategies</h2>
            {strategies.length === 0 ? (
              <p className="text-gray-500">No strategies saved yet. Create your first one!</p>
            ) : (
              <ul className="space-y-2">
                {strategies.map(strategy => (
                  <li 
                    key={strategy.id} 
                    className={`p-3 rounded-lg cursor-pointer transition-all duration-300 transform ${selectedStrategy?.id === strategy.id ? 'bg-blue-500/10 border-l-4 border-blue-500 shadow-md' : 'hover:bg-white/40 hover:-translate-y-0.5'}`}
                    onClick={() => handleSelectStrategy(strategy.id!)}
                  >
                    <div className="font-medium">{strategy.recommended_strategy}</div>
                    <div className="text-sm text-gray-600">
                      {strategy.business_type} - {strategy.product_name}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Main content */}
          <div className="lg:col-span-2">
            <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg p-6">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-blue-500 opacity-5"></div>
              <h2 className="text-xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                {selectedStrategy ? 'Edit Strategy' : 'New Strategy'}
              </h2>
              
              {/* Business Information */}
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3 flex items-center">
                  <span className="w-8 h-8 rounded-lg mr-2 flex items-center justify-center text-white bg-gradient-to-br from-blue-400 to-indigo-500 shadow-md">
                    <span className="text-md">💼</span>
                  </span>
                  Business Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Type*</label>
                    <input
                      type="text"
                      value={businessType}
                      onChange={(e) => setBusinessType(e.target.value)}
                      className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      placeholder="e.g., SaaS, Retail, Service"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product/Service Name*</label>
                    <input
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      placeholder="e.g., Project Management Software"
                      required
                    />
                  </div>
                </div>
              </div>
              
              {/* AI Strategy Generation */}
              {!selectedStrategy || !recommendedStrategy ? (
                <div className="mb-6 relative overflow-hidden rounded-xl bg-gradient-to-br from-green-50 to-blue-50 p-5 border border-green-100/80 shadow-md">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-blue-400 opacity-5"></div>
                  <h3 className="text-lg font-medium mb-3 flex items-center">
                    <span className="w-8 h-8 rounded-lg mr-2 flex items-center justify-center text-white bg-gradient-to-br from-yellow-400 to-amber-500 shadow-md">
                      <FaLightbulb className="text-white" />
                    </span>
                    Generate Strategy with AI
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target Market*</label>
                      <input
                        type="text"
                        value={targetMarket}
                        onChange={(e) => setTargetMarket(e.target.value)}
                        className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                        placeholder="e.g., Small businesses, Enterprise customers"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Business Goals*</label>
                      <textarea
                        value={businessGoals}
                        onChange={(e) => setBusinessGoals(e.target.value)}
                        className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                        rows={2}
                        placeholder="e.g., Market penetration, Maximize profit"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Competitor Information (Optional)</label>
                      <textarea
                        value={competitorInfo}
                        onChange={(e) => setCompetitorInfo(e.target.value)}
                        className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                        rows={2}
                        placeholder="e.g., Main competitors and their pricing"
                      />
                    </div>
                    <div className="pt-2">
                      <button
                        onClick={handleGenerateStrategy}
                        disabled={generating}
                        className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg flex items-center shadow-lg hover:shadow-green-500/50 transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-70 disabled:hover:transform-none"
                      >
                        {generating ? (
                          <>
                            <FaSpinner className="animate-spin mr-2" /> Generating...
                          </>
                        ) : (
                          <>
                            <FaLightbulb className="mr-2" /> Generate Strategy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
              
              {/* Strategy Details */}
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3 flex items-center">
                  <span className="w-8 h-8 rounded-lg mr-2 flex items-center justify-center text-white bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
                    <span className="text-md">📋</span>
                  </span>
                  Strategy Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Recommended Strategy*</label>
                    <input
                      type="text"
                      value={recommendedStrategy}
                      onChange={(e) => setRecommendedStrategy(e.target.value)}
                      className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      placeholder="e.g., Premium Pricing"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Strategy Description*</label>
                    <textarea
                      value={strategyDescription}
                      onChange={(e) => setStrategyDescription(e.target.value)}
                      className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      rows={4}
                      placeholder="Detailed description of the pricing strategy"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Implementation Notes</label>
                    <textarea
                      value={implementationNotes}
                      onChange={(e) => setImplementationNotes(e.target.value)}
                      className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      rows={3}
                      placeholder="Practical steps to implement this strategy"
                    />
                  </div>
                </div>
              </div>
              
              {/* Action buttons */}
              <div className="flex justify-end space-x-3">
                {selectedStrategy && (
                  <button
                    onClick={handleDeleteStrategy}
                    className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg flex items-center shadow-lg hover:shadow-red-500/50 transition-all duration-300 transform hover:-translate-y-0.5"
                  >
                    <FaTrash className="mr-2" /> Delete
                  </button>
                )}
                <button
                  onClick={handleSaveStrategy}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg flex items-center shadow-lg hover:shadow-blue-500/50 transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  <FaSave className="mr-2" /> {selectedStrategy ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </section>
  );
};

export default PricingStrategyAdvisor;
